import { Leaf } from 'lucide-react';
import { cn } from '@/lib/utils';

export type LogoSize = 'sm' | 'md' | 'lg';

const sizeMap: Record<LogoSize, { box: string; icon: number; text: string }> = {
  sm: { box: 'w-9 h-9', icon: 18, text: 'text-lg' },
  md: { box: 'w-10 h-10', icon: 20, text: 'text-xl' },
  lg: { box: 'w-12 h-12', icon: 24, text: 'text-2xl' },
};

interface LogoProps {
  /** Mark + wordmark size preset. */
  size?: LogoSize;
  /** Whether to render the "Asogro" wordmark next to the mark. */
  showWordmark?: boolean;
  /** Extra classes on the wrapping element. */
  className?: string;
  /** Extra classes on the orange mark square. */
  markClassName?: string;
}

/**
 * Single source of truth for the Asogro brand mark.
 * Use everywhere the logo appears so size, radius, and colors stay consistent.
 */
export const Logo: React.FC<LogoProps> = ({
  size = 'md',
  showWordmark = true,
  className,
  markClassName,
}) => {
  const s = sizeMap[size];
  return (
    <span className={cn('flex items-center gap-2.5', className)}>
      <span
        className={cn(
          'bg-primary-500 rounded-xl flex items-center justify-center text-white shadow-lg shadow-primary-500/25 shrink-0',
          s.box,
          markClassName,
        )}
      >
        <Leaf size={s.icon} strokeWidth={2.5} />
      </span>
      {showWordmark && (
        <span className={cn('font-display font-bold tracking-tight text-secondary-900 dark:text-white', s.text)}>
          Aso<span className="text-primary-500">gro</span>
        </span>
      )}
    </span>
  );
};
